import { isPromiseLike } from '@precon-tools-suite-by-shared/js-utils';
import { AuthorizationError } from '../errors/AuthorizationError';
import type {
    AbilityChecker,
    AbilityInspector,
    AuthorizationResult,
} from '../types';
import type { AbilityContext, Ability, AsyncAbility } from '../types';
import { createAuthorizationResult } from './response-handling';

export function inspectAbility<TParams extends any[]>(
    context: AbilityContext,
    ability: Ability<TParams>,
    ...parameters: TParams
): AuthorizationResult;
export function inspectAbility<TParams extends any[]>(
    context: AbilityContext,
    ability: AsyncAbility<TParams>,
    ...parameters: TParams
): Promise<AuthorizationResult>;
export function inspectAbility<TParams extends any[]>(
    context: AbilityContext,
    ability: Ability<TParams> | AsyncAbility<TParams>,
    ...parameters: TParams
): AuthorizationResult | Promise<AuthorizationResult> {
    if (context.isSuperUser)
        return Object.assign(Promise.resolve({ allowed: true }), {
            allowed: true,
        }) as AuthorizationResult;

    return createAuthorizationResult(
        context,
        ability(context, ...parameters) as any
    );
}

export async function inspectAsyncAbility<TParams extends any[]>(
    context: AbilityContext,
    ability: AsyncAbility<TParams>,
    ...parameters: TParams
): Promise<AuthorizationResult> {
    if (context.isSuperUser) return { allowed: true };

    return createAuthorizationResult(context, ability(context, ...parameters));
}

export function createAbilityInspector(
    context: AbilityContext
): AbilityInspector {
    return ((ability: any, ...params: any[]) =>
        inspectAbility(context, ability, ...params)) as AbilityInspector;
}

export function createAbilityChecker(context: AbilityContext): AbilityChecker {
    return ((ability: any, ...params: any[]) => {
        const result: any = inspectAbility(context, ability, ...params);
        if ('allowed' in result) {
            return result.allowed;
        } else {
            return result.then((res: AuthorizationResult) => res.allowed);
        }
    }) as AbilityChecker;
}

export function authorizeAbility<TParams extends any[]>(
    context: AbilityContext,
    ability: Ability<TParams>,
    ...parameters: TParams
): void;
export function authorizeAbility<TParams extends any[]>(
    context: AbilityContext,
    ability: AsyncAbility<TParams>,
    ...parameters: TParams
): Promise<void>;
export function authorizeAbility<TParams extends any[]>(
    context: AbilityContext,
    ability: Ability<TParams> | AsyncAbility<TParams>,
    ...parameters: TParams
): void | Promise<void> {
    const result: AuthorizationResult | Promise<AuthorizationResult> =
        inspectAbility(context, ability as Ability<TParams>, ...parameters);

    if (result.allowed) return Promise.resolve();

    if (isPromiseLike<AuthorizationResult>(result)) {
        return result.then((res) => {
            if (res.allowed) return;
            return Promise.reject(new AuthorizationError(res));
        }) as Promise<void>;
    }

    if (!result.allowed) {
        throw new AuthorizationError(result);
    }
}
